import React, { useMemo, useCallback } from 'react';
import { mergeStyles } from '@cian/utils';

import { EPageKind } from '../../../types/page';
import { Slider } from '../../Slider';
import { Device } from '../utils/useMediaQuery';
import { GlamorousCard } from '../GlamorousCard';
import { trackSliderClick } from '../tracking/trackSliderClick';
import { addTypesToCards } from './utils/addTypesToCards';
import { combineHorizontalCards } from './utils/combineHorizontalCards';
import { isHorizontalCard } from './utils/isHorizontalCard';
import { GLAMOROUS_CARDS, LAPTOP_CARDS_ORDER, DESKTOP_CARDS_ORDER } from './cards';

import styles from './GlamorousCards.css';

interface IGlamorousCardsProps {
  pageKind: EPageKind;
  device: Device;
}

const SLIDER_CLASSES = {
  group: styles['slider-group'],
  groupsContainer: styles['slider-groups-container'],
};

const LaptopGlamorousCards = (props: { pageKind: EPageKind }) => {
  const { pageKind } = props;

  const typedCards = useMemo(() => addTypesToCards(GLAMOROUS_CARDS, LAPTOP_CARDS_ORDER), []);
  const trackPrevButtonClick = useCallback(() => trackSliderClick({ pageKind, buttonType: 'prev' }), []);
  const trackNextButtonClick = useCallback(() => trackSliderClick({ pageKind, buttonType: 'next' }), []);

  return (
    <Slider
      visibleSlidesCount={3}
      step={3}
      classes={SLIDER_CLASSES}
      onPrevButtonClick={trackPrevButtonClick}
      onNextButtonClick={trackNextButtonClick}
    >
      {typedCards.map(typedCard => {
        const isVertical = !isHorizontalCard(typedCard);
        const { className } = mergeStyles(isVertical && styles['laptop-vartical-card']);

        return <GlamorousCard key={typedCard.title} className={className} pageKind={pageKind} {...typedCard} />;
      })}
    </Slider>
  );
};

const DesktopGlamorousCards = (props: { pageKind: EPageKind }) => {
  const { pageKind } = props;

  const typedCards = useMemo(() => addTypesToCards(GLAMOROUS_CARDS, DESKTOP_CARDS_ORDER), []);
  const combinedCards = useMemo(() => combineHorizontalCards(typedCards), []);
  const trackPrevButtonClick = useCallback(() => trackSliderClick({ pageKind, buttonType: 'prev' }), []);
  const trackNextButtonClick = useCallback(() => trackSliderClick({ pageKind, buttonType: 'next' }), []);

  return (
    <Slider
      visibleSlidesCount={3}
      step={3}
      classes={SLIDER_CLASSES}
      onPrevButtonClick={trackPrevButtonClick}
      onNextButtonClick={trackNextButtonClick}
    >
      {combinedCards.map(combinedCard => {
        const { nextCard, ...card } = combinedCard;
        const isHorizontal = isHorizontalCard(card);
        const { className } = mergeStyles(
          isHorizontal && styles['desktop-horizontal-card'],
          !isHorizontal && styles['desktop-vartical-card'],
        );

        if (isHorizontal && nextCard) {
          const { className: doubleCardClassName } = mergeStyles(styles['double-horizontal-card'], className);

          return (
            <div key={card.title} className={doubleCardClassName}>
              <GlamorousCard pageKind={pageKind} {...card} />
              <GlamorousCard pageKind={pageKind} {...nextCard} />
            </div>
          );
        }

        return <GlamorousCard key={card.title} className={className} pageKind={pageKind} {...card} />;
      })}
    </Slider>
  );
};

export const GlamorousCards = ({ pageKind, device }: IGlamorousCardsProps) => {
  if (device === 'laptop') {
    return <LaptopGlamorousCards pageKind={pageKind} />;
  }

  if (device === 'desktop') {
    return <DesktopGlamorousCards pageKind={pageKind} />;
  }

  return null;
};
